import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { requestAPI, requestTypeAPI, userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { PlusIcon } from '@heroicons/react/24/outline';

const Requests = () => {
  const { isIT, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [itUsers, setItUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: searchParams.get('category') || '',
    requestTypeId: ''
  });

  // Status change modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState({
    requestId: null,
    newStatus: '',
    statusNote: '',
    resolution: ''
  });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchRequestTypes();
    if (isIT()) {
      fetchItUsers();
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [filters]);

  const fetchRequestTypes = async () => {
    try {
      const response = await requestTypeAPI.getAll({ isActive: true });
      setRequestTypes(response.data);
    } catch (error) {
      console.error('Request types fetch error:', error);
    }
  };

  const fetchItUsers = async () => {
    try {
      const response = await userAPI.getAll({ role: 'it', isActive: true });
      const adminResponse = await userAPI.getAll({ role: 'admin', isActive: true });
      setItUsers([...response.data, ...adminResponse.data]);
    } catch (error) {
      console.error('IT users fetch error:', error);
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach(key => !params[key] && delete params[key]);
      const response = await requestAPI.getAll(params);

      // Filter out resolved and closed requests for active list (IT users see them in resolved page)
      let filteredRequests = response.data;
      if (!filters.status) {
        // If no specific status filter, hide resolved/closed
        filteredRequests = response.data.filter(r => !['resolved', 'closed'].includes(r.status));
      }

      setRequests(filteredRequests);
    } catch (error) {
      toast.error('Talepler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const openStatusModal = (requestId, newStatus) => {
    setStatusChangeData({
      requestId,
      newStatus,
      statusNote: '',
      resolution: ''
    });
    setShowStatusModal(true);
  };

  const handleStatusChange = async () => {
    if (!statusChangeData.statusNote.trim()) {
      toast.error('Lütfen durum değişikliği açıklaması girin');
      return;
    }

    if (statusChangeData.newStatus === 'resolved' && !statusChangeData.resolution.trim()) {
      toast.error('Çözüm açıklaması zorunludur');
      return;
    }

    setUpdating(true);
    try {
      await requestAPI.update(statusChangeData.requestId, {
        status: statusChangeData.newStatus,
        statusNote: statusChangeData.statusNote,
        resolution: statusChangeData.newStatus === 'resolved' ? statusChangeData.resolution : undefined
      });
      toast.success('Durum güncellendi');
      setShowStatusModal(false);
      fetchRequests();
    } catch (error) {
      toast.error('Durum güncellenirken hata');
    } finally {
      setUpdating(false);
    }
  };

  const handleAssign = async (requestId, assignedToId) => {
    try {
      await requestAPI.assign(requestId, { assignedToId });
      toast.success('Talep atandı');
      fetchRequests();
    } catch (error) {
      toast.error('Atama başarısız');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Açık' },
      in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'İşlemde' },
      pending: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Beklemede' },
      resolved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Çözüldü' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Kapatıldı' }
    };
    const c = config[status] || config.open;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Düşük' },
      medium: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Orta' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Yüksek' },
      critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Kritik' }
    };
    const c = config[priority] || config.medium;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Talepler</h1>
        <div className="flex gap-2">
          <Link
            to="/requests/new"
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Yeni Talep
          </Link>
          <Link
            to="/requests/new?type=issue"
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Arıza Bildir
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tüm Kategoriler</option>
            <option value="request">Talepler</option>
            <option value="issue">Arızalar</option>
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Aktif Durumlar</option>
            <option value="open">Açık</option>
            <option value="in_progress">İşlemde</option>
            <option value="pending">Beklemede</option>
            <option value="resolved">Çözüldü</option>
            <option value="closed">Kapatıldı</option>
          </select>

          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tüm Öncelikler</option>
            <option value="low">Düşük</option>
            <option value="medium">Orta</option>
            <option value="high">Yüksek</option>
            <option value="critical">Kritik</option>
          </select>

          <select
            value={filters.requestTypeId}
            onChange={(e) => setFilters({ ...filters, requestTypeId: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tüm Türler</option>
            {requestTypes.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Request List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            Talep bulunamadı
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Talep</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tür</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Talep Eden</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Öncelik</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                  {isIT() && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Atanan</th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">İşlem</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      #{request.id}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{request.title}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">{request.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${request.requestType?.category === 'issue' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                        {request.requestType?.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{request.requester?.fullName}</div>
                      <div className="text-xs text-gray-500">{request.requester?.department}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPriorityBadge(request.priority)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isIT() ? (
                        <select
                          value={request.status}
                          onChange={(e) => openStatusModal(request.id, e.target.value)}
                          className="text-xs border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="open">Açık</option>
                          <option value="in_progress">İşlemde</option>
                          <option value="pending">Beklemede</option>
                          <option value="resolved">Çözüldü</option>
                          <option value="closed">Kapatıldı</option>
                        </select>
                      ) : (
                        getStatusBadge(request.status)
                      )}
                    </td>
                    {isIT() && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={request.assignedToId || ''}
                          onChange={(e) => handleAssign(request.id, e.target.value)}
                          className="text-xs border border-gray-300 rounded px-2 py-1 max-w-[120px]"
                        >
                          <option value="">Atanmadı</option>
                          {itUsers.map(u => (
                            <option key={u.id} value={u.id}>{u.fullName}</option>
                          ))}
                        </select>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(request.createdAt), 'dd.MM.yyyy HH:mm')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/requests/${request.id}`}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Detay
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <h3 className="text-lg font-semibold mb-4">Durum Değiştir</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Yeni Durum
              </label>
              <div className="text-sm font-medium">
                {statusChangeData.newStatus === 'open' && 'Açık'}
                {statusChangeData.newStatus === 'in_progress' && 'İşlemde'}
                {statusChangeData.newStatus === 'pending' && 'Beklemede'}
                {statusChangeData.newStatus === 'resolved' && 'Çözüldü'}
                {statusChangeData.newStatus === 'closed' && 'Kapatıldı'}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Açıklama *
              </label>
              <textarea
                value={statusChangeData.statusNote}
                onChange={(e) => setStatusChangeData({ ...statusChangeData, statusNote: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="Bu durum değişikliğinin nedenini açıklayın..."
              />
            </div>

            {statusChangeData.newStatus === 'resolved' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Çözüm Açıklaması *
                </label>
                <textarea
                  value={statusChangeData.resolution}
                  onChange={(e) => setStatusChangeData({ ...statusChangeData, resolution: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
                  placeholder="Yapılan işlemleri ve çözümü açıklayın..."
                />
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={updating}
              >
                İptal
              </button>
              <button
                onClick={handleStatusChange}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={updating}
              >
                {updating ? 'Kaydediliyor...' : 'Durumu Güncelle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Requests;
